import {
   DocumentSendError,
   MedicalLeaveForGuardianApproveCommand,
   PueConnectionFault,
   PueFault,
   ZlaDocumentsSendFault,
} from '@dreryk/egabinet-ezla-frontend-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import { commonTranslationKeys, medicalLeaveFormTranslationKeys } from '../../../../../translations';
import {
   mapMedicalLeaveFormAddressToPatientAddress,
   mapMedicalLeavePuePayersToMedicalLeavePayerApprove,
} from '../../../common/common.utils';
import { findEndDate, findStartDate } from '../../../common/formSections/leaveDateRange/leaveDateRange.utils';
import { GuardianMedicalLeaveFormBean } from '../../guardianMedicalLeave.types';

type UseMedicalLeaveForGuardianApproveCommandOptions = {
   onSuccess: () => void;
   onPueFault: (fault: PueFault) => void;
   onZlaDocumentSendFault: (zlaDocumentSendErrors: DocumentSendError[]) => void;
   onAuthenticationRequiredFault: () => void;
};

type MedicalLeaveForGuardianApproveCommandParameters = {
   externalId: string;
   formData: GuardianMedicalLeaveFormBean;
};

export const useMedicalLeaveGuardianApproveCommand = (
   options: UseMedicalLeaveForGuardianApproveCommandOptions
): UseCqrsCommandResult<
   void,
   MedicalLeaveForGuardianApproveCommandParameters,
   CertificateFault | PueFault | PueConnectionFault | ZlaDocumentsSendFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new MedicalLeaveForGuardianApproveCommand();
         command.externalId = parameters.externalId;
         const guardian = parameters.formData.guardian!;
         command.medicalLeaveForGuardianDetails = {
            birthDate: Choice.withUseDefaultIfNull(parameters.formData.birthDate ?? null),
            address: mapMedicalLeaveFormAddressToPatientAddress(parameters.formData.address!),
            dateFrom: findStartDate(parameters.formData.leaveDateRange)!,
            dateTo: findEndDate(parameters.formData.leaveDateRange)!,
            insuranceType: parameters.formData.insuranceType,
            payers: mapMedicalLeavePuePayersToMedicalLeavePayerApprove(
               parameters.formData.payers,
               parameters.formData.leaveAfterTerminationOfEmployment
            ),
            reason: Choice.withUseDefaultIfNull(parameters.formData.reason),
            guardianExternalIdentifier: guardian.guardianExternalIdentifier,
            firstName: FirstName.of(guardian.firstName!),
            middleName: Choice.withUseDefaultIfNull(guardian.middleName ? MiddleName.of(guardian.middleName) : null),
            lastName: LastName.of(guardian.lastName!),
            relationCode: parameters.formData.relationCode,
         };
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(commonTranslationKeys.submitSuccess);
            options.onSuccess();
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: options.onAuthenticationRequiredFault,
            }),
            'egabinet-ezla-frontend-api:PueConnectionFault': () => {
               dispatchSnackbarNegativeEvent(medicalLeaveFormTranslationKeys.pueNotAvailable);
            },
            'egabinet-ezla-frontend-api:PueFault': (fault) => {
               dispatchSnackbarNegativeEvent(commonTranslationKeys.submitError);
               options.onPueFault(fault);
            },
            'egabinet-ezla-frontend-api:ZlaDocumentsSendFault': (fault) => {
               options.onZlaDocumentSendFault(fault.errors!);
            },
         },
      }
   );
};
